import type { FoldCell } from "@pdfking/book3d"
import type { Radian } from "@pdfking/math"
import { PI, Sign } from "@pdfking/math"

import { StandingMeet } from "./StandingMeet"

/**
 * A cover's `folds` from its spine, the first, its boards that never open
 * stood off it till they meet over it, a desk calendar's base: each step 0
 * fold beside the spine across the opening turned past square by its share
 * of the angle they meet at; a turn-in off the spine's ends stays as it
 * folds. `opening`, when two stand, what they meet at over the top, short of
 * a turn: the pages open as far as they stand apart.
 */
export const StandBoards = <F extends FoldCell>(
  folds: F[],
  opening_top: boolean
): { folds: F[]; opening: Radian | undefined } => {
  const spine = folds[0]
  if (!spine) return { folds, opening: undefined }
  const along = ({ box }: FoldCell) => (opening_top ? box.height : box.width)
  // Its span across the opening; beside the spine, its middle within the spine's
  const span = ({ box }: FoldCell) =>
    opening_top ? [box.x, box.x + box.width] : [box.y, box.y + box.height]
  const [from, to] = span(spine)
  const beside = (fold: FoldCell) => {
    const [start, end] = span(fold)
    const middle = (start! + end!) / 2
    return middle > from! && middle < to!
  }
  const standing = folds.filter(fold => fold.parent === 0 && !fold.step && beside(fold))
  const meet = (fold: FoldCell) => StandingMeet(along(spine), along(fold))
  return {
    folds: folds.map(fold =>
      standing.includes(fold)
        ? { ...fold, angle: (Sign(fold.angle) * (PI / 2 + meet(fold))) as Radian }
        : fold
    ),
    opening:
      standing.length === 2
        ? ((2 * PI - meet(standing[0]!) - meet(standing[1]!)) as Radian)
        : undefined,
  }
}
